import { useState } from "react";
import { useMatch } from "react-router";
import { ArrowForward, ChevronRight } from "@mui/icons-material";

import analytics from "fond/analytics";
import { EntityType } from "fond/types";
import localStorage from "fond/utils/localStorage";

import { SidebarSection } from "./types";

import {
  NavigationAccordion,
  NavigationAccordionDetails,
  NavigationAccordionSummary,
  NoItemsMessage,
  StyledNavigationLink,
} from "./NavigationLinkMenu.styles";

export type NavigationLinkMenuItem = {
  ID: string;
  url: string;
  label?: string;
  entityType?: EntityType;
  icon?: React.ReactNode;
  onClick?: (event: React.MouseEvent) => void;
  rightAdornment?: React.ReactNode;
};

export type NavigationLinkProps = {
  /**
   * The URL to navigate to when the link is clicked.
   */
  to: string;
  /** The label of the navigation link menu */
  label?: string;
  /**
   * The message to display when there are no items in the menu.
   */
  emptyMessage: string;
  /**
   * The items to be displayed in the navigation link menu.
   */
  items: NavigationLinkMenuItem[];
  /**
   * A unique identifier for the navigation link menu, used to differentiate the menu from others and storing its state.
   */
  id: Exclude<SidebarSection, "main">;
  /**
   * The text to display for the "See all" link at the bottom of the menu.
   * If not provided or empty string, the link will not be shown. Defaults to "See all".
   */
  seeAllText?: string;
  /**
   * Whether to show the "See all" link even when there are no items.
   * Defaults to false.
   */
  showSeeAllWhenEmpty?: boolean;
};

const STORAGE_KEY = "FOND_SIDEBAR_MENU_STATE";

type SidebarState = Record<Exclude<SidebarSection, "main">, boolean>;

export const NavigationLinkMenu: React.FC<NavigationLinkProps> = ({
  label,
  to,
  emptyMessage,
  items,
  id,
  seeAllText = "See all",
  showSeeAllWhenEmpty = false,
}: NavigationLinkProps) => {
  const matches = useMatch(to ?? "unknown");
  const hasItems = items.length > 0;
  const localStorageState: SidebarState = localStorage.getItem(STORAGE_KEY) ?? {};
  const testId = `navigation-link-menu-${id}`;
  const panelId = `navigation-panel-${id}-content`;
  const headerId = `navigation-panel-${id}-header`;

  // always inially expand if the current route matches the link
  const initialExpandState = localStorageState[id] || !!matches;
  const [isExpanded, setIsExpanded] = useState<boolean>(initialExpandState);

  const accordionChange = (_: React.SyntheticEvent, expanded: boolean) => {
    localStorage.setItem(STORAGE_KEY, { ...localStorage.getItem(STORAGE_KEY), [id]: expanded });
    setIsExpanded(expanded);
    if (expanded) {
      analytics.track("sidebar.section.expanded", { source: id });
    } else {
      analytics.track("sidebar.section.collapsed", { source: id });
    }
  };

  const trackSeeAllClick = () => {
    analytics.track("sidebar.see-all.clicked", {
      source: id,
    });
  };

  const trackItemClickForItem = () => {
    analytics.track("sidebar.menu-item.clicked", {
      entityId: items[0].ID,
      entityType: "project" as EntityType, // Assuming all items are projects, adjust as necessary
      source: id,
    });
  };

  return (
    <NavigationAccordion disableGutters expanded={isExpanded} data-testid={testId} onChange={accordionChange}>
      <NavigationAccordionSummary expandIcon={<ChevronRight fontSize="small" />} aria-controls={panelId} id={headerId}>
        {label}
      </NavigationAccordionSummary>
      <NavigationAccordionDetails>
        {hasItems ? (
          items.map(({ ID, url, icon, label: itemLabel, onClick, rightAdornment }) => (
            <StyledNavigationLink
              key={ID}
              to={url}
              icon={icon}
              rightAdornment={rightAdornment}
              onClick={(e) => {
                trackItemClickForItem();
                onClick?.(e);
              }}
            >
              {itemLabel}
            </StyledNavigationLink>
          ))
        ) : (
          <NoItemsMessage>{emptyMessage}</NoItemsMessage>
        )}
        {(hasItems || showSeeAllWhenEmpty) && seeAllText && (
          <StyledNavigationLink to={to} className="see-all" icon={<ArrowForward color="primary" />} iconPosition="end" onClick={trackSeeAllClick}>
            {seeAllText}
          </StyledNavigationLink>
        )}
      </NavigationAccordionDetails>
    </NavigationAccordion>
  );
};
